import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, Button, Chip, CircularProgress, Alert } from '@mui/material'
import { FiLock, FiCheckCircle } from 'react-icons/fi'
import { MembershipPlan } from '@/types/subscription'
import { AcknowledgementRecord } from '@/types/subscriptionAgreement'
import { membershipApi } from '@/services/api'
import { ConsentService } from '@/services/consentService'
import { fetchUserIpAddress } from '@/utils/ipAddress'
import { ACKNOWLEDGEMENT_GROUPS, ACKNOWLEDGEMENT_POINTS } from './subscriptionAgreementContent'
import AcknowledgementPoint from './AcknowledgementPoint'
import { isInitialFilled } from './acknowledgementUtils'
import AgreementSigningModal from './AgreementSigningModal'
import AdministratorCertification from './AdministratorCertification'

const AGREEMENT_VERSION = 'v1.0'

interface SubscriptionAgreementPanelProps {
  selectedPlan: MembershipPlan | null
  billingCycle: 'monthly' | 'yearly'
  // Grand total for the whole order (unit price x provider count, after any
  // coupon) — as returned by the plan-picker dialog. Divide by providerCount
  // to get the per-user rate shown here; it is NOT itself a per-user price.
  discountedPrice?: number | null
  providerCount?: number
  agencyName: string
  // Agency id returned by agency signup — used to fetch the DocuSign
  // signing URL for this agency's subscription agreement.
  agencyId?: string | null
  // Primary Agency Administrator's name, captured at signup — the
  // certification name/title are derived from this rather than retyped.
  administratorFirstName?: string
  administratorLastName?: string
  // True while handleActivateSubscription is creating the Stripe
  // subscription/payment intent, after "Activate subscription" is clicked.
  activating?: boolean
  onComplete: () => void
  hasSignedAgreement?: boolean
}

const TOTAL_POINTS = ACKNOWLEDGEMENT_POINTS.length

const SubscriptionAgreementPanel: React.FC<SubscriptionAgreementPanelProps> = ({
  selectedPlan,
  billingCycle,
  discountedPrice,
  providerCount,
  agencyName,
  agencyId,
  administratorFirstName,
  administratorLastName,
  activating,
  onComplete,
  hasSignedAgreement,
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [signed, setSigned] = useState(hasSignedAgreement ?? false)
  const [signedAt, setSignedAt] = useState<string | null>(null)
  const [acknowledgements, setAcknowledgements] = useState<Record<string, AcknowledgementRecord>>({})
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [autopayAuthorized, setAutopayAuthorized] = useState(false)
  const [eSignConsent, setESignConsent] = useState(false)
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [ipAddress, setIpAddress] = useState('')
  const [submittingAgreement, setSubmittingAgreement] = useState(false)
  const [agreementSubmitError, setAgreementSubmitError] = useState<string | null>(null)

  // Certification name/title come from the signup record, not retyped here.
  const administratorName = `${administratorFirstName ?? ''} ${administratorLastName ?? ''}`.trim()
  const administratorTitle = 'Primary Administrator'

  useEffect(() => {
    fetchUserIpAddress().then(setIpAddress)
  }, [])

  useEffect(() => {
    if (hasSignedAgreement) {
      setSigned(true)
    }
  }, [hasSignedAgreement])

  const handleInitialChange = (pointId: string, value: string) => {
    setAcknowledgements(prev => ({
      ...prev,
      [pointId]: {
        pointId,
        initials: value,
        timestamp: isInitialFilled(value) ? new Date().toISOString() : '',
      },
    }))
  }

  const handleSigned = () => {
    setModalOpen(false)
    setSigned(true)
    setSignedAt(new Date().toISOString())
  }

  // Records the signed agreement (signature, initials, certification) before
  // handing off to onComplete, which creates the Stripe subscription/payment
  // intent and opens the payment modal.
  const handleActivateClick = async () => {
    if (!agencyId) return
    setAgreementSubmitError(null)
    setSubmittingAgreement(true)
    try {
      const resolvedIpAddress = ipAddress || (await fetchUserIpAddress())
      await ConsentService.submitAgencySubscriptionAgreement({
        userId: agencyId,
        planId: selectedPlan?._id ?? '',
        agreementVersion: AGREEMENT_VERSION,
        fullName: administratorName,
        ipAddress: resolvedIpAddress,
        userAgent: navigator.userAgent,
        consentTime: new Date().toISOString(),
        signatureType: 'drawn',
        drawnSignature: signatureDataUrl ?? '',
        initial_1: acknowledgements['point-1']?.initials ?? '',
        initial_2: acknowledgements['point-2']?.initials ?? '',
        initial_3: acknowledgements['point-3']?.initials ?? '',
        initial_4: acknowledgements['point-4']?.initials ?? '',
        initial_5: acknowledgements['point-5']?.initials ?? '',
        initial_6: acknowledgements['point-6']?.initials ?? '',
        initial_7: acknowledgements['point-7']?.initials ?? '',
        initial_8: acknowledgements['point-8']?.initials ?? '',
        initial_9: acknowledgements['point-9']?.initials ?? '',
        initial_10: acknowledgements['point-10']?.initials ?? '',
        agencyName,
        adminName: administratorName,
        adminTitle: administratorTitle,
      })
      onComplete()
    } catch (error) {
      console.error('Failed to record subscription agreement signature:', error)
      setAgreementSubmitError('Failed to save your signed agreement. Please try again.')
    } finally {
      setSubmittingAgreement(false)
    }
  }

  // Shared by "Download unsigned/signed PDF" and "View agreement" — the
  // endpoint returns a presigned S3 URL for the PDF, which we just open in
  // a new tab (the browser's PDF viewer covers both viewing and saving).
  const handleOpenAgreementPdf = async () => {
    if (!agencyId) return
    setDownloadingPdf(true)
    try {
      const { signedUrl } = await membershipApi.downloadAgencyAgreement(agencyId)
      window.open(signedUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Failed to open agreement PDF:', error)
    } finally {
      setDownloadingPdf(false)
    }
  }

  const initialedCount = useMemo(
    () => ACKNOWLEDGEMENT_POINTS.filter(p => isInitialFilled(acknowledgements[p.id]?.initials || '')).length,
    [acknowledgements]
  )
  const allInitialed = initialedCount === TOTAL_POINTS

  // One signer, one set of initials — the first initials entered set the
  // standard, and every other point must match it exactly.
  const referenceInitials = useMemo(() => {
    const firstFilled = ACKNOWLEDGEMENT_POINTS.find(p => isInitialFilled(acknowledgements[p.id]?.initials || ''))
    return firstFilled ? acknowledgements[firstFilled.id].initials : null
  }, [acknowledgements])

  const allInitialsMatch = useMemo(
    () =>
      referenceInitials == null ||
      ACKNOWLEDGEMENT_POINTS.every(p => {
        const v = acknowledgements[p.id]?.initials || ''
        return !isInitialFilled(v) || v === referenceInitials
      }),
    [acknowledgements, referenceInitials]
  )

  const readyToActivate =
    signed &&
    allInitialed &&
    allInitialsMatch &&
    administratorName.trim() !== '' &&
    administratorTitle.trim() !== '' &&
    !!signatureDataUrl &&
    autopayAuthorized &&
    eSignConsent

  const footnote = !signed
    ? 'Sign the agreement to begin.'
    : !allInitialed
      ? `${TOTAL_POINTS - initialedCount} point${TOTAL_POINTS - initialedCount === 1 ? '' : 's'} left to initial.`
      : !allInitialsMatch
        ? 'Your initials must be the same on every point.'
        : !administratorName.trim() || !administratorTitle.trim()
          ? 'Add your name and title to finish.'
          : !signatureDataUrl
            ? 'Draw your signature to finish.'
            : !autopayAuthorized
              ? 'Authorize recurring charges to finish.'
              : !eSignConsent
                ? 'Confirm electronic signature consent to finish.'
                : 'All set — everything is complete.'

  // selectedPlan.monthly_fee is the plan's list price per user. discountedPrice
  // (when present) is the coupon-adjusted GRAND TOTAL for the whole order, so
  // it has to be divided back down by the provider count to get a per-user
  // rate — dividing by 0/undefined would be wrong, so guard to at least 1.
  const listPricePerUser = selectedPlan?.monthly_fee
  const safeProviderCount = providerCount && providerCount > 0 ? providerCount : 1
  const discountedPricePerUser =
    discountedPrice != null ? discountedPrice / safeProviderCount : null
  const displayedPrice = discountedPricePerUser ?? listPricePerUser
  const displayedPriceLabel =
    displayedPrice != null
      ? Number.isInteger(displayedPrice)
        ? displayedPrice.toString()
        : displayedPrice.toFixed(2)
      : null
  const systemFeePercent = selectedPlan?.monthly_commission ?? selectedPlan?.commission

  // The actual monthly charge for the whole order — per-user rate x provider
  // count. Prefer the real total passed down (covers any coupon); fall back
  // to computing it from the plan's list price if that's not available yet.
  const totalMonthlyAmount =
    discountedPrice ?? (listPricePerUser != null ? listPricePerUser * safeProviderCount : null)
  const totalMonthlyAmountLabel =
    totalMonthlyAmount != null
      ? Number.isInteger(totalMonthlyAmount)
        ? totalMonthlyAmount.toString()
        : totalMonthlyAmount.toFixed(2)
      : null

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#7A6FA1' }}>
            Subscription Agreement
          </Typography>
          <Typography sx={{ fontSize: '13.5px', color: '#7C8794', fontWeight: 600 }}>
            (Required to activate your subscription)
          </Typography>
        </Box>
        <Chip
          label={`${initialedCount} / ${TOTAL_POINTS} initialed`}
          sx={{
            bgcolor: allInitialed ? '#EAF7F0' : '#F3F5F7',
            color: allInitialed ? '#1E7A4E' : '#4B5563',
            fontWeight: 700,
            border: '1px solid',
            borderColor: allInitialed ? '#A9DCC0' : '#E6E9ED',
          }}
        />
      </Box>

      <Box sx={{ bgcolor: 'white', border: '1px solid #E6E9ED', borderRadius: 2, p: 3, mb: 2 }}>
        <Typography sx={{ fontSize: '15.5px', color: '#2C333D', mb: 1.5 }}>
          Before your agency&rsquo;s subscription begins, the Agency Administrator needs to sign the{' '}
          <strong>Agency Platform Subscription Agreement</strong> — the contract covering what you pay, how seats
          are added and removed, how payments settle, and what PillarThera does and does not guarantee.
        </Typography>
        <Typography sx={{ fontSize: '15.5px', color: '#2C333D', mb: 1.5 }}>
          After signing, you&rsquo;ll initial ten short statements covering the terms most likely to affect your
          monthly bill or come up later. They&rsquo;re summaries written for quick review; the signed agreement is
          what governs.
        </Typography>
        <Typography sx={{ fontSize: '14.5px', color: '#7C8794' }}>
          Only the <strong>Primary Agency Administrator</strong> can complete this step. You will find the signed copy in your email and in <em>Agency Portal → Documents</em>.
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          border: '1px solid #C3B8E4',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 2,
          bgcolor: 'white',
        }}
      >
        <Box sx={{ p: 2.5, borderRight: { sm: '1px solid #E6E9ED' }, borderBottom: { xs: '1px solid #E6E9ED', sm: 'none' } }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#7A6FA1', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Per user
          </Typography>
          <Typography sx={{ fontSize: '25px', fontWeight: 800, color: '#2C333D' }}>
            {displayedPriceLabel != null ? `$${displayedPriceLabel}` : '—'}
            <Box component="span" sx={{ fontSize: '15px', fontWeight: 600, color: '#7C8794' }}>/month</Box>
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#7C8794' }}>Providers and office users alike</Typography>
        </Box>
        <Box sx={{ p: 2.5, borderRight: { sm: '1px solid #E6E9ED' }, borderBottom: { xs: '1px solid #E6E9ED', sm: 'none' } }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#7A6FA1', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            PER-CLAIM SYSTEM FEE
          </Typography>
          <Typography sx={{ fontSize: '25px', fontWeight: 800, color: '#2C333D' }}>
            {systemFeePercent != null ? `${systemFeePercent}%` : '—'}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#7C8794' }}>On everything you collect</Typography>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#7A6FA1', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            Billing
          </Typography>
          <Typography sx={{ fontSize: '25px', fontWeight: 800, color: '#2C333D' }}>
            {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'}
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: '#7C8794' }}>Auto-renewal & charged, cancel anytime</Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          bgcolor: '#EEEBF7',
          border: '1px solid #C3B8E4',
          borderRadius: 2,
          p: 2,
          mb: 2,
        }}
      >
        <Typography sx={{ fontSize: '14px', color: '#4B5563' }}>
          {displayedPriceLabel != null && `$${displayedPriceLabel}`} × {safeProviderCount} provider
          {safeProviderCount === 1 ? '' : 's'}/office users
        </Typography>
        <Typography sx={{ fontSize: '20px', fontWeight: 800, color: '#5B4C8F' }}>
          Total: {totalMonthlyAmountLabel != null ? `$${totalMonthlyAmountLabel}/month` : '—'}
        </Typography>
      </Box>

      <Box sx={{ bgcolor: 'white', border: '1px solid #E6E9ED', borderRadius: 2, p: 3, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#2C333D', mb: 0.5 }}>
          1. Read and sign the agreement
        </Typography>
        <Typography sx={{ fontSize: '14.5px', color: '#7C8794', mb: 2 }}>
          The agreement will be open in a secure DocuSign window. Download a copy before  and/or after you sign the agreement.
        </Typography>

        {!signed ? (
          <>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={() => setModalOpen(true)}
                disabled={!agencyId}
                sx={{
                  bgcolor: '#7A6FA1',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  '&:hover': { bgcolor: '#5B4C8F' },
                  '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
                }}
              >
                Open agreement &amp; sign
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenAgreementPdf}
                disabled={downloadingPdf}
                startIcon={downloadingPdf ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  px: 3,
                  borderColor: '#CFD6DE',
                  color: '#2C333D',
                }}
              >
                {downloadingPdf ? 'Downloading…' : 'Download unsigned PDF'}
              </Button>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#FEF6E7',
                border: '1px solid #F0D7A4',
                color: '#8A6414',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              Not signed yet — required before your subscription activates
            </Box>
          </>
        ) : (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: '#EAF7F0',
                border: '1px solid #A9DCC0',
                color: '#1E7A4E',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <FiCheckCircle /> Signed — a copy has been emailed and saved to your Documents
            </Box>

            {signedAt && (
              <Typography sx={{ mt: 1.5, fontSize: '13px', color: '#7C8794' }}>
                Signed {new Date(signedAt).toLocaleString()} by the Primary Agency Administrator
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleOpenAgreementPdf}
                disabled={downloadingPdf}
                startIcon={downloadingPdf ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : undefined}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, borderColor: '#CFD6DE', color: '#2C333D' }}
              >
                {downloadingPdf ? 'Downloading…' : 'Download signed PDF'}
              </Button>
              <Button
                variant="outlined"
                onClick={handleOpenAgreementPdf}
                disabled={downloadingPdf}
                sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, px: 3, borderColor: '#CFD6DE', color: '#2C333D' }}
              >
                View agreement
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {!signed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            bgcolor: '#F3F5F7',
            border: '1px dashed #CFD6DE',
            borderRadius: 2,
            p: 2,
            mb: 2,
            color: '#7C8794',
            fontSize: '14px',
          }}
        >
          <FiLock /> Sign the agreement above to unlock the key points.
        </Box>
      )}

      <Box sx={{ opacity: signed ? 1 : 0.42, pointerEvents: signed ? 'auto' : 'none', filter: signed ? 'none' : 'saturate(0.4)' }}>
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '18px', color: '#2C333D' }}>
            2. Initial the ten key points
          </Typography>
          <Typography sx={{ fontSize: '14.5px', color: '#7C8794' }}>
            Grouped into four areas. Enter your initials on each to confirm you understand it.
          </Typography>
        </Box>

        {ACKNOWLEDGEMENT_GROUPS.map(group => {
          const groupPoints = ACKNOWLEDGEMENT_POINTS.filter(p => p.group === group.id).map(p => {
            // point-1's price is written against the plan the agency actually
            // chose, not a fixed "$50" — substitute the resolved per-user rate.
            if (p.id !== 'point-1') return p
            const priceForCopy = displayedPriceLabel ?? '50'
            return {
              ...p,
              title: `$${priceForCopy} per user, per month — monthly billing only`,
              body: [
                `Every user account costs $${priceForCopy}/month at the standard rate, whether that person is a provider or an office user. Job title and hours worked don't change the price. Billing is monthly only — there is no annual or prepaid plan — and it renews automatically on the same date each month until I cancel.`,
              ],
            }
          })
          const groupDone = groupPoints.filter(p => isInitialFilled(acknowledgements[p.id]?.initials || '')).length
          return (
            <Box key={group.id} sx={{ mt: 3.5 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  pb: 1,
                  mb: 1.25,
                  borderBottom: '2px solid #EEEBF7',
                }}
              >
                <Typography sx={{ fontSize: '12.5px', fontWeight: 800, color: '#7A6FA1', textTransform: 'uppercase', letterSpacing: '0.09em' }}>
                  {group.label}
                </Typography>
                <Typography sx={{ fontSize: '12.5px', fontWeight: 600, color: groupDone === groupPoints.length ? '#1E7A4E' : '#7C8794' }}>
                  {groupDone} of {groupPoints.length}
                </Typography>
              </Box>
              {groupPoints.map(point => (
                <AcknowledgementPoint
                  key={point.id}
                  point={point}
                  value={acknowledgements[point.id]?.initials || ''}
                  onChange={handleInitialChange}
                  expectedInitials={referenceInitials}
                />
              ))}
            </Box>
          )
        })}

        <AdministratorCertification
          agencyName={agencyName}
          administratorName={administratorName}
          administratorTitle={administratorTitle}
          hasSignature={!!signatureDataUrl}
          onSignatureChange={setSignatureDataUrl}
          autopayAuthorized={autopayAuthorized}
          onAutopayChange={setAutopayAuthorized}
          eSignConsent={eSignConsent}
          onESignChange={setESignConsent}
        />
      </Box>

      {agreementSubmitError && (
        <Alert severity="error" onClose={() => setAgreementSubmitError(null)} sx={{ mb: 2 }}>
          {agreementSubmitError}
        </Alert>
      )}

      <Box
        sx={{
          // position: 'sticky',
          // bottom: 0,
          bgcolor: 'rgba(255,255,255,0.96)',
          borderTop: '1px solid #E6E9ED',
          mt: 4,
          py: 2,
          px: { xs: 0, sm: 1 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '13.5px', color: '#7C8794' }}>{footnote}</Typography>
        <Button
          variant="contained"
          size="large"
          disabled={!readyToActivate || submittingAgreement || activating}
          onClick={handleActivateClick}
          startIcon={
            submittingAgreement || activating ? (
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
            ) : undefined
          }
          sx={{
            bgcolor: '#7A6FA1',
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            px: 4,
            '&:hover': { bgcolor: '#5B4C8F' },
            '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500' },
          }}
        >
          {submittingAgreement ? 'Submitting agreement…' : activating ? 'Activating…' : 'Activate subscription'}
        </Button>
      </Box>

      <AgreementSigningModal
        open={modalOpen}
        agencyId={agencyId}
        onClose={() => setModalOpen(false)}
        onSigned={handleSigned}
      />
    </Box>
  )
}

export default SubscriptionAgreementPanel
